// Input validation utilities

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validateEmail = (email: string): ValidationResult => {
  const errors: string[] = [];
  
  if (!email) {
    errors.push('メールアドレスは必須です');
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      errors.push('有効なメールアドレスを入力してください');
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

export const validatePhone = (phone: string): ValidationResult => {
  const errors: string[] = [];
  
  if (phone) {
    const phoneRegex = /^[0-9\-+()\s]+$/;
    if (!phoneRegex.test(phone) || phone.length < 10) {
      errors.push('有効な電話番号を入力してください');
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

export const validateRequired = (value: string, fieldName: string): ValidationResult => {
  const errors: string[] = [];
  
  if (!value || value.trim().length === 0) {
    errors.push(`${fieldName}は必須です`);
  }
  
  return { isValid: errors.length === 0, errors };
};

export const validateNumber = (value: number, min?: number, max?: number, fieldName?: string): ValidationResult => {
  const errors: string[] = [];
  const field = fieldName || '数値';
  
  if (isNaN(value)) {
    errors.push(`${field}は有効な数値を入力してください`);
  } else {
    if (min !== undefined && value < min) {
      errors.push(`${field}は${min}以上を入力してください`);
    }
    if (max !== undefined && value > max) {
      errors.push(`${field}は${max}以下を入力してください`);
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

export const validateUrl = (url: string): ValidationResult => {
  const errors: string[] = [];
  
  if (url) {
    try {
      new URL(url);
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        errors.push('URLはhttp://またはhttps://で始まる必要があります');
      }
    } catch {
      errors.push('有効なURLを入力してください');
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

export const validateApiKey = (key: string, type: 'line' | 'openai' | 'claude'): ValidationResult => {
  const errors: string[] = [];
  
  if (key) {
    switch (type) {
      case 'openai':
        if (!key.startsWith('sk-') || key.length < 40) {
          errors.push('有効なOpenAI APIキー形式ではありません');
        }
        break;
      case 'line':
        if (key.length < 100) {
          errors.push('有効なLINE アクセストークン形式ではありません');
        }
        break;
      case 'claude':
        if (key.length < 20) {
          errors.push('有効なClaude APIキー形式ではありません');
        }
        break;
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

export const sanitizeHtml = (input: string): string => {
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
};

export const validateAndSanitizeInput = (input: string, maxLength: number = 1000): string => {
  if (!input) return '';
  
  // Remove potentially dangerous characters
  let sanitized = input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '');
  
  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized.trim();
};